import { useState, useEffect, useRef } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowRight, ChevronRight, ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CookieBanner, CookieSettingsLink } from "@/components/CookieBanner";
import GoogleTagManager from "@/components/GoogleTagManager";
import AnnouncementBar from "@/components/public/AnnouncementBar";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

/* Default nav links when none configured in DB */
const DEFAULT_NAV_LINKS = [
  { label: "Conócenos", link: "/conocenos", order: 2, visible: true, show_mobile: true },
  { label: "Servicios Postventa", link: "#", order: 3, visible: true, show_mobile: true,
    children: [
      { label: "Mecánica y mantenimiento", link: "/mecanica", visible: true },
      { label: "Chapa y pintura", link: "/chapa-y-pintura", visible: true },
      { label: "Recambios", link: "/recambios", visible: true },
      { label: "Ofertas Postventa", link: "/promociones-postventa", visible: true },
    ]},
  { label: "Vehículos", link: "/vehiculos", order: 4, visible: true, show_mobile: true },
  { label: "Contacto", link: "/contacto", order: 5, visible: true, show_mobile: true },
];


/* Horarios por sede: inline si hay 1-2 centros y total < 80 chars, dropdown si más */
function HeaderSchedules({ centers, fallback }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!centers || centers.length === 0) {
    if (!fallback) return null;
    return (
      <span className="flex items-center gap-1.5" data-testid="top-hours">
        <Clock className="w-3 h-3" /> {fallback}
      </span>
    );
  }

  // Inline si total ≤ 2 centros y longitud razonable
  const inlineText = centers.map((c) => `${c.label}: L-V ${c.hours}`).join(" · ");
  const useInline = centers.length <= 2 && inlineText.length <= 70;

  if (useInline) {
    return (
      <span className="flex items-center gap-1.5" data-testid="top-hours">
        <Clock className="w-3 h-3" />
        <span data-testid="top-hours-inline">{inlineText}</span>
      </span>
    );
  }

  return (
    <div ref={ref} className="relative" data-testid="top-hours-dropdown">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 hover:text-white transition-colors"
        data-testid="top-hours-toggle"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Clock className="w-3 h-3" /> Horarios centros
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[240px] bg-white text-slate-900 rounded-sm shadow-lg border border-slate-200 p-3 z-50"
          data-testid="top-hours-panel"
        >
          {centers.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
              <span className="text-xs font-semibold text-slate-700">{c.label}</span>
              <span className="text-xs text-slate-500">L-V {c.hours}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* Dropdown for desktop nav */
function DropdownNav({ item, location }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const isChildActive = item.children.some(c => location.pathname === c.link);
  const menuId = `dropdown-${item.label.replace(/\s/g, "-").toLowerCase()}`;

  // Click outside
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") { setOpen(false); ref.current?.querySelector("button")?.focus(); } };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const scheduleOpen = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setOpen(false), 80); };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div ref={ref} className="relative" data-testid="dropdown-servicios">
      <button
        onClick={() => setOpen(o => !o)}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(o => !o); }
          if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); }
        }}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={menuId}
        className={`inline-flex items-center gap-1 rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-3 transition-colors ${isChildActive || open ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`}
        data-testid="dropdown-trigger">
        {item.label}
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute top-full left-0 pt-1 w-64"
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
        >
          <div
            id={menuId}
            role="menu"
            className="bg-white border border-slate-200 shadow-xl rounded-sm z-[100]"
            data-testid="dropdown-menu"
          >
            <div className="py-1">
              {item.children.filter(c => c.visible !== false).map((child, idx) => (
                <Link key={child.link} to={child.link}
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => setOpen(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") { e.preventDefault(); setOpen(false); ref.current?.querySelector("button")?.focus(); }
                    if (e.key === "ArrowDown") { e.preventDefault(); e.currentTarget.nextElementSibling?.focus(); }
                    if (e.key === "ArrowUp") { e.preventDefault(); e.currentTarget.previousElementSibling?.focus(); }
                  }}
                  className={`block px-4 py-2.5 text-xs uppercase tracking-wider font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-inset ${location.pathname === child.link ? "bg-red-50 text-red-700 border-l-2 border-red-600" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent"}`}
                  data-testid={`dropdown-item-${child.link.replace(/\//g, "")}`}>
                  {child.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Mobile nav with hamburger */
function MobileNav({ siteContent, location }) {
  const [open, setOpen] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState(null);

  const navLinks = (siteContent?.nav_links?.length ? siteContent.nav_links : DEFAULT_NAV_LINKS)
    .filter(n => n.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="md:hidden flex items-center gap-1.5">
      {(siteContent?.header_config?.cta_visible !== false) && (
        <Link to={siteContent?.header_config?.cta_link || "/cita"}>
          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-[10px] uppercase tracking-wider font-bold h-8 px-3" data-testid="mobile-cta">
            {siteContent?.header_config?.cta_mobile_text || "Cita"}
          </Button>
        </Link>
      )}
      <button onClick={() => setOpen(!open)} className="w-9 h-9 flex items-center justify-center text-slate-700" data-testid="mobile-menu-toggle">
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {open && (
        <div className="fixed inset-x-0 top-16 bg-white border-t border-slate-200 shadow-xl z-50 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
          <div className="py-2">
            {navLinks.map(item => {
              if (item.children && item.children.length > 0) {
                const isExpanded = expandedDropdown === item.label;
                return (
                  <div key={item.label}>
                    <button onClick={() => setExpandedDropdown(isExpanded ? null : item.label)}
                      className="w-full flex items-center justify-between px-5 py-3 text-xs uppercase tracking-wider font-semibold text-slate-700 hover:bg-slate-50"
                      data-testid="mobile-dropdown-trigger">
                      {item.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                    {isExpanded && (
                      <div className="bg-slate-50 border-t border-b border-slate-100">
                        {item.children.filter(c => c.visible !== false).map(child => (
                          <Link key={child.link} to={child.link} onClick={() => setOpen(false)}
                            className={`block px-8 py-2.5 text-xs uppercase tracking-wider font-medium ${location.pathname === child.link ? "text-red-600 font-semibold" : "text-slate-500 hover:text-slate-800"}`}>
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link key={item.link} to={item.link} onClick={() => setOpen(false)}
                  className={`block px-5 py-3 text-xs uppercase tracking-wider font-semibold ${location.pathname === item.link ? "text-red-600 bg-red-50" : "text-slate-700 hover:bg-slate-50"}`}>
                  {item.mobile_label || item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicLayout() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [config, setConfig] = useState(null);
  const [centers, setCenters] = useState([]);
  const [branding, setBranding] = useState(null);
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/config`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/centers?active_only=true`).then(r => r.data).catch(() => []),
      axios.get(`${API}/api/branding`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/site-content`).then(r => r.data).catch(() => null),
    ]).then(([cfg, ctr, br, sc]) => { setConfig(cfg); setCenters(ctr); setBranding(br); setSiteContent(sc); });
  }, []);

  const companyName = config?.company_name || "ASCAUTO";
  const companyShort = config?.company_short || "AS";
  const tagline = config?.tagline || "";
  const phone = config?.phone || "";
  const brands = config?.brands || [];
  const legalText = config?.legal_text || "";
  const footerText = config?.footer_text || "";
  const social = config?.social_media || {};
  const firstCenter = centers[0];
  const hours = firstCenter?.working_hours || config?.working_hours || {};

  // Format hours for header bar (fallback)
  const weekdayHour = hours.lunes || hours.martes || "";
  const headerHours = weekdayHour && weekdayHour !== "cerrado" ? `L-V: ${weekdayHour}` : "";
  const centerCities = centers.map(c => c.city || c.name).join(" | ");
  const hb = siteContent?.header_brand || {};

  // ── Horarios por sede para la cabecera pública ──
  // Solo centros activos + show_in_header !== false + con horario L-V definido.
  const _hoursForHeader = (c) => {
    const wh = c.working_hours || {};
    const candidates = [wh.lunes, wh.martes, wh.miercoles, wh.miércoles, wh.jueves, wh.viernes];
    const first = candidates.find((v) => v && String(v).toLowerCase().trim() !== "cerrado");
    return first || "";
  };
  const headerCenters = (centers || [])
    .filter((c) => c.active !== false && c.show_in_header !== false)
    .map((c) => ({
      id: c.id,
      label: c.city || c.name,
      hours: _hoursForHeader(c),
      header_order: c.header_order ?? c.order ?? 0,
    }))
    .filter((c) => c.hours)
    .sort((a, b) => (a.header_order || 0) - (b.header_order || 0));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <GoogleTagManager />
      {/* Iter 207 — Avisos web públicos: barra superior negra */}
      <AnnouncementBar position="top_bar" />
      {/* Top bar */}
      <div className="bg-slate-900 text-slate-400 text-[11px] font-medium hidden sm:block" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-5">
            {phone && (
              <a href={`tel:${phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="top-phone">
                <Phone className="w-3 h-3" /> {phone}
              </a>
            )}
            {centerCities && (
              <span className="flex items-center gap-1.5" data-testid="top-locations">
                <MapPin className="w-3 h-3" /> {centerCities}
              </span>
            )}
          </div>
          <HeaderSchedules centers={headerCenters} fallback={headerHours} />
        </div>
      </div>

      {/* Navigation */}
      <header className={`sticky top-0 z-50 transition-colors duration-300 ${isHome ? 'bg-white/80 backdrop-blur-lg border-b border-slate-200/60' : 'bg-white border-b border-slate-200'}`} data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            {(hb.show_logo !== false) && (branding?.logo_url ? (
              <img src={getImageUrl(branding.logo_url)} alt={companyName} className="h-9 w-auto object-contain" data-testid="header-logo-img" />
            ) : (
              <div className="w-9 h-9 bg-red-600 rounded-sm flex items-center justify-center">
                <span className="text-white font-extrabold text-sm">{companyShort}</span>
              </div>
            ))}
            {hb.show_text !== false && (
              <div className="flex flex-col">
                <span className="heading-industrial text-xl font-extrabold text-slate-900 uppercase tracking-tight leading-none">{hb.brand_text || companyName}</span>
                {hb.show_tagline !== false && (hb.tagline_text || tagline) && <span className="text-[8px] text-slate-400 uppercase tracking-[0.2em] font-medium leading-none mt-0.5">{hb.tagline_text || tagline}</span>}
              </div>
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            {(siteContent?.nav_links?.length ? siteContent.nav_links : DEFAULT_NAV_LINKS)
              .filter(n => n.visible !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(item => {
                if (item.children && item.children.length > 0) {
                  return <DropdownNav key={item.label} item={item} location={location} />;
                }
                return (
                  <Link key={item.link} to={item.link}>
                    <Button variant="ghost" className={`rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-3 ${location.pathname === item.link ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            {(siteContent?.header_config?.cta_visible !== false) && (
              <Link to={siteContent?.header_config?.cta_link || "/cita"} className="ml-1">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider font-bold h-9 px-5" data-testid="nav-cta">
                  {siteContent?.header_config?.cta_text || "Reservar Cita"}
                </Button>
              </Link>
            )}
          </nav>

          {/* Mobile nav */}
          <MobileNav siteContent={siteContent} location={location} />
        </div>
      </header>

      {/* Iter 207 — Avisos web públicos: cajón debajo del menú */}
      <AnnouncementBar position="drawer" />

      {/* Main content */}
      <main className="flex-1"><Outlet /></main>

      {/* Cookie Banner */}
      <CookieBanner />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {/* Brand column */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                {(hb.show_logo !== false) && (branding?.logo_dark_url || branding?.logo_url ? (
                  <img src={getImageUrl(branding.logo_dark_url || branding.logo_url)} alt={companyName} className="h-9 w-auto object-contain brightness-0 invert" data-testid="footer-logo-img" />
                ) : (
                  <div className="w-9 h-9 bg-red-600 rounded-sm flex items-center justify-center">
                    <span className="text-white font-extrabold text-sm">{companyShort}</span>
                  </div>
                ))}
                {hb.show_text !== false && <span className="heading-industrial text-xl font-extrabold text-white uppercase">{hb.brand_text || companyName}</span>}
              </div>
              <p className="text-xs leading-relaxed mb-4">{siteContent?.footer_description || footerText}</p>
              {brands.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {brands.filter(b => b.name).map(b => (
                    <span key={b.name} className="text-[9px] uppercase tracking-wider font-medium border border-slate-700 px-2 py-0.5 rounded-sm">{b.name}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Centers columns */}
            {centers.slice(0, 2).map(c => (
              <div key={c.id}>
                <h4 className="heading-industrial text-sm font-bold text-white uppercase mb-4">{c.name}</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-500" /><span>{c.address}<br />{c.zip_code} {c.city}</span></div>
                  {c.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 flex-shrink-0 text-red-500" /><a href={`tel:${c.phone.replace(/\s/g,'')}`} className="hover:text-white transition-colors">{c.phone}</a></div>}
                </div>
              </div>
            ))}

            {/* Links column */}
            <div>
              <h4 className="heading-industrial text-sm font-bold text-white uppercase mb-4">Enlaces</h4>
              <div className="space-y-2">
                {(siteContent?.footer_links || [
                  { to: "/", label: "Inicio" }, { to: "/vehiculos", label: "Vehículos" },
                  { to: "/contacta-asesor", label: "Habla con tu Asesor" }, { to: "/cita", label: "Reservar Cita" },
                ]).map(l => (
                  <Link key={l.to || l.link} to={l.to || l.link} className="flex items-center gap-1.5 text-xs hover:text-white transition-colors">
                    <ChevronRight className="w-3 h-3 text-red-500" /> {l.label}
                  </Link>
                ))}
              </div>

              {/* Legal links */}
              <h5 className="text-xs font-semibold text-white uppercase mt-5 mb-2">Legal</h5>
              <div className="space-y-2">
                {[
                  { to: "/politica-de-privacidad", label: "Politica de privacidad" },
                  { to: "/aviso-legal", label: "Aviso legal" },
                  { to: "/politica-de-cookies", label: "Politica de cookies" },
                ].map(l => (
                  <Link key={l.to} to={l.to} className="flex items-center gap-1.5 text-xs hover:text-white transition-colors">
                    <ChevronRight className="w-3 h-3 text-red-500" /> {l.label}
                  </Link>
                ))}
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <ChevronRight className="w-3 h-3 text-red-500" />
                  <CookieSettingsLink className="text-slate-400" />
                </div>
              </div>
              {/* Social */}
              {Object.values(social).some(v => v) && (
                <div className="mt-6">
                  <h5 className="text-xs font-semibold text-white uppercase mb-2">Siguenos</h5>
                  <div className="flex gap-3">
                    {Object.entries(social).filter(([,v]) => v).map(([net, url]) => (
                      <a key={net} href={url} target="_blank" rel="noopener noreferrer" className="text-xs hover:text-white transition-colors capitalize">{net}</a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Legal bottom bar - always visible */}
        <div className="border-t border-slate-800" data-testid="footer-legal">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-[10px] text-slate-500 text-center sm:text-left uppercase tracking-wider">
                {siteContent?.footer_legal_text || legalText || `${new Date().getFullYear()} ${companyName} - Servicio Oficial Stellantis`}
              </p>
              <div className="flex items-center gap-4" data-testid="footer-legal-links">
                <Link to="/politica-de-privacidad" className="text-[10px] text-slate-500 hover:text-white transition-colors uppercase tracking-wider">Privacidad</Link>
                <Link to="/aviso-legal" className="text-[10px] text-slate-500 hover:text-white transition-colors uppercase tracking-wider">Aviso legal</Link>
                <Link to="/politica-de-cookies" className="text-[10px] text-slate-500 hover:text-white transition-colors uppercase tracking-wider">Cookies</Link>
                <CookieSettingsLink className="text-[10px] text-slate-500 uppercase tracking-wider" />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
